/**
 * Emit a standalone SVG-string for the active diagram.
 *
 * The canvas itself is rendered by React Flow as a mix of HTML <div>'s
 * (nodes) plus SVG <path>'s (edges) — there's no single SVG to grab. So
 * we re-build a self-contained SVG from the same domain-state the canvas
 * uses: positions and sizes from the diagram's `viewState`, names and
 * types from the workspace store.
 *
 * Output is intentionally simple and self-contained: no external CSS, no
 * <foreignObject>, no React-attributes. The user can copy-paste into
 * Inkscape, Figma, slides, Markdown — anywhere SVG is accepted.
 *
 * Trade-offs vs. trying to snapshot the live DOM (e.g. via html-to-image):
 * - ✅ Bundle-size: no extra dependency
 * - ✅ Deterministic: same input → identical output, useful for diffs
 * - ✅ No browser-quirks (Safari foreignObject font issues, etc.)
 * - ❌ Doesn't capture per-node visual overrides like custom fontSize or
 *   fill color set via the toolbar (those live in viewState.nodeStyle but
 *   aren't read here yet — TODO for later iteration)
 * - ❌ Edge-routing is straight-line (no smoothstep / waypoints) — future
 *   enhancement
 */

import type { Concept, Diagram, Relation } from "@ae/domain";
import { legacyTypeIdFor } from "@ae/domain";

interface EmitOptions {
  diagram: Diagram;
  concepts: Concept[];
  relations: Relation[];
  /** UI language for resolving bilingual labels. */
  lang: "nl" | "en";
}

/** Default size for nodes that don't have an explicit width/height stored. */
const DEFAULT_W = 140;
const DEFAULT_H = 60;
const PADDING = 24;

const LAYER_FILLS: Record<string, string> = {
  business: "#FFF6B0", // soft yellow
  application: "#C7DFFC", // soft blue
  technology: "#C5E8C5", // soft green
  motivation: "#DCC8E5", // soft mauve
  strategy: "#FCDBB7", // soft orange
  implementation: "#FCEBC7", // soft beige
  composite: "#E2E8F0", // soft gray
};

const LAYER_STROKES: Record<string, string> = {
  business: "#E5C500",
  application: "#5B8DEF",
  technology: "#4FAA5E",
  motivation: "#9F70B5",
  strategy: "#E69A4D",
  implementation: "#D9B66B",
  composite: "#94A3B8",
};

/** XML-escape a string so it's safe inside SVG text/attribute values. */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function resolveLabel(name: { nl?: string; en?: string } | undefined, lang: "nl" | "en"): string {
  if (!name) return "";
  return name[lang] || name[lang === "nl" ? "en" : "nl"] || Object.values(name)[0] || "";
}

/**
 * Walk the parent chain in viewState to compute a node's absolute (canvas)
 * position. React Flow stores nested-node positions as relative to their
 * parent; we need absolute coords for SVG.
 */
function absolutePosition(diagram: Diagram, key: string): { x: number; y: number } | null {
  const ve = diagram.viewState[key];
  if (!ve?.position) return null;
  let x = ve.position.x;
  let y = ve.position.y;
  let cur = ve;
  while (cur.containerId) {
    const parent = diagram.viewState[cur.containerId];
    if (!parent?.position) break;
    x += parent.position.x;
    y += parent.position.y;
    cur = parent;
  }
  return { x, y };
}

export function emitDiagramSvg(opts: EmitOptions): string {
  const { diagram, concepts, relations, lang } = opts;
  const conceptById = new Map(concepts.map((c) => [c.id, c]));

  // Pass 1: collect all visible nodes with absolute positions + sizes.
  // The viewState entry's KEY is the node-instance id; entries with a
  // `conceptRef` reference an underlying concept that may differ from the
  // key (multi-instance support).
  type Node = {
    key: string;
    conceptId: string;
    name: string;
    layer: string;
    x: number;
    y: number;
    w: number;
    h: number;
  };
  const nodes: Node[] = [];
  const nodeByKey = new Map<string, Node>();
  for (const [key, ve] of Object.entries(diagram.viewState)) {
    if (ve.position === undefined) continue;
    if (ve.hidden === true) continue;
    const conceptId = ve.ref ?? key;
    const concept = conceptById.get(conceptId);
    if (!concept) continue;
    const abs = absolutePosition(diagram, key);
    if (!abs) continue;
    const node: Node = {
      key,
      conceptId,
      name: resolveLabel(concept.name, lang),
      // Pull layer from typeId prefix as a heuristic — we don't have
      // direct access to the registry here, but the prefix usually
      // encodes the aspect-pack which is "archimate.<TypeName>".
      // For accurate layer-color mapping, callers should provide a
      // resolved layer per-node — but for this first pass the heuristic
      // is good enough.
      layer: guessLayer(concept.typeId, concept.stereotypes),
      x: abs.x,
      y: abs.y,
      w: ve.width ?? DEFAULT_W,
      h: ve.height ?? DEFAULT_H,
    };
    nodes.push(node);
    nodeByKey.set(key, node);
  }

  // Compute viewBox (bounding box of all nodes + padding).
  if (nodes.length === 0) {
    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="200" height="80" viewBox="0 0 200 80">
  <text x="100" y="44" text-anchor="middle" font-family="sans-serif" font-size="13" fill="#94a3b8">leeg diagram</text>
</svg>`;
  }
  const minX = Math.min(...nodes.map((n) => n.x)) - PADDING;
  const minY = Math.min(...nodes.map((n) => n.y)) - PADDING;
  const maxX = Math.max(...nodes.map((n) => n.x + n.w)) + PADDING;
  const maxY = Math.max(...nodes.map((n) => n.y + n.h)) + PADDING;
  const vbW = maxX - minX;
  const vbH = maxY - minY;

  // Build edges. For each relation, resolve source/target node positions
  // and draw a straight line between centres.
  const edges: string[] = [];
  for (const r of relations) {
    if (r.tombstoneInBranch) continue;
    if (diagram.viewState[r.id]?.hidden === true) continue;
    // Find a visible node for the source/target concept. Prefer the
    // canonical placement (key === conceptId) but fall back to any
    // viewState entry that visualises this concept.
    const findNodeFor = (conceptId: string): Node | undefined => {
      const direct = nodeByKey.get(conceptId);
      if (direct) return direct;
      return nodes.find((n) => n.conceptId === conceptId);
    };
    const src = findNodeFor(r.sourceConceptId);
    const dst = findNodeFor(r.targetConceptId);
    if (!src || !dst) continue;
    const x1 = src.x + src.w / 2;
    const y1 = src.y + src.h / 2;
    const x2 = dst.x + dst.w / 2;
    const y2 = dst.y + dst.h / 2;
    edges.push(
      `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#475569" stroke-width="1.5" marker-end="url(#arrow)" />`,
    );
  }

  // Emit nodes (rect + text). Edges go first so they render under nodes.
  const nodeEls: string[] = [];
  for (const n of nodes) {
    const fill = LAYER_FILLS[n.layer] ?? LAYER_FILLS.composite;
    const stroke = LAYER_STROKES[n.layer] ?? LAYER_STROKES.composite;
    const cx = n.x + n.w / 2;
    const cy = n.y + n.h / 2;
    // Truncate long names so they don't overflow the rect — first cut
    // is a hard char-limit; a future iteration could wrap to multiple
    // lines via <tspan>.
    const label = n.name.length > 28 ? `${n.name.slice(0, 26)}…` : n.name;
    nodeEls.push(
      `<g><rect x="${n.x}" y="${n.y}" width="${n.w}" height="${n.h}" rx="6" ry="6" fill="${fill}" stroke="${stroke}" stroke-width="2" /><text x="${cx}" y="${cy + 4}" text-anchor="middle" font-family="ui-sans-serif, system-ui, sans-serif" font-size="12" font-weight="600" fill="#0f172a">${esc(label)}</text></g>`,
    );
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${Math.round(vbW)}" height="${Math.round(vbH)}" viewBox="${minX} ${minY} ${vbW} ${vbH}">
  <defs>
    <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
      <path d="M 0 0 L 10 5 L 0 10 z" fill="#475569" />
    </marker>
  </defs>
${edges.map((e) => `  ${e}`).join("\n")}
${nodeEls.map((n) => `  ${n}`).join("\n")}
</svg>`;
}

/**
 * Guess the layer from the typeId prefix. Returns one of the LAYER_*
 * keys; falls back to "composite" for unknown packs.
 */
function guessLayer(typeId: string, stereotypes: readonly string[] = []): string {
  // Sinds de ArchiMate-4-merge zegt de typenaam niets meer: `archimate.Process`
  // bevat noch "business" noch "application", en viel dus door naar
  // "composite" (grijs). Het compat-profiel houdt de oorspronkelijke laag wel
  // vast, dus vertaal eerst daarnaar terug — dan werkt de naam-heuristiek
  // hieronder weer zoals bedoeld.
  const resolved = legacyTypeIdFor(typeId, stereotypes);
  if (resolved !== typeId) return guessLayerByName(resolved);
  return guessLayerByName(typeId);
}

function guessLayerByName(typeId: string): string {
  // Heuristic — the registry's element types live under e.g.
  // "archimate.BusinessProcess" with `layer: "business"` in the JSON.
  // Without runtime registry access here we map by name-prefix.
  const lower = typeId.toLowerCase();
  if (lower.includes("business")) return "business";
  if (lower.includes("application")) return "application";
  if (
    lower.includes("technology") ||
    lower.includes("artifact") ||
    lower.includes("node") ||
    lower.includes("device") ||
    lower.includes("systemsoftware") ||
    lower.includes("communicationnetwork")
  )
    return "technology";
  if (
    lower.includes("goal") ||
    lower.includes("driver") ||
    lower.includes("assessment") ||
    lower.includes("outcome") ||
    lower.includes("principle") ||
    lower.includes("requirement") ||
    lower.includes("constraint") ||
    lower.includes("stakeholder") ||
    lower.includes("value")
  )
    return "motivation";
  if (
    lower.includes("courseofaction") ||
    lower.includes("capability") ||
    lower.includes("resource") ||
    lower.includes("valuestream")
  )
    return "strategy";
  if (
    lower.includes("workpackage") ||
    lower.includes("deliverable") ||
    lower.includes("plateau") ||
    lower.includes("gap") ||
    lower.includes("implementation")
  )
    return "implementation";
  return "composite";
}
